// ─────────────────────────────────────────────────────────────────────────────
// ALT ADMİN + ŞUBE YÖNETİCİSİ İŞLEM LOGLAYICI
//
// Alt admin (role=subadmin) ve şube yöneticisi (role=manager) oturumlarında
// yapılan TÜM yazma isteklerini (POST/PUT/DELETE/PATCH) toplayıp batch halinde
// gts_hr_api'ye gönderir (rol → endpoint haritası: subadmin-activity/log,
// manager-activity/log). Ana admin bu kayıtları Kullanıcılar sayfasındaki
// "İşlem Geçmişi" modalında (Alt Adminler / Şube Yöneticileri sekmeleri) görür.
//
// Kayıt noktası: config/apiClient.ts response interceptor'ı — 27 API istemcisinin
// tamamı oradan geçtiği için tek noktadan yakalanır (recordApiWrite çağrısı).
//
// ⚠️ DAİRESEL İMPORT GÜVENLİĞİ: Bu modül apiClient'ı import ETMEZ (apiClient bizi
// import eder). Gönderim RAW axios + HRAPI_URL ile yapılır; Authorization header'ı
// ownAuthSession üzerinden eklenir.
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios';
import { HRAPI_URL } from '../config/api';
import { describeActivity, isReadOnlyApiPath } from './activityLogDescriptions';
import {
  decodeJwt,
  getAccessToken,
  getStoredAccessToken,
} from './auth/ownAuthSession';

/**
 * Rol → log endpoint haritası. Haritada olmayan roller loglanmaz.
 * Rol login sırasında sabit olduğundan bir oturumda pratikte tek endpoint'e akar;
 * yine de flush endpoint bazında gruplar (oturum değişimi yarışlarına dayanıklı).
 */
const ROLE_LOG_ENDPOINTS: Record<string, string> = {
  subadmin: `${HRAPI_URL}/api/hr/subadmin-activity/log`,
  manager: `${HRAPI_URL}/api/hr/manager-activity/log`,
};
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 10;
/** Backend tek batch'te en fazla 50 kabul eder (SubAdmin/ManagerActivityController.MaxBatchSize). */
const MAX_BATCH_SEND = 50;
/** Flush sürekli başarısız olursa kuyruk sınırsız büyümesin — en eskiler atılır. */
const MAX_QUEUE_SIZE = 300;

const WRITE_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

/** Loglanmayacak path'ler: log uçlarının kendisi (sonsuz döngü) + auth gürültüsü (refresh/login). */
const EXCLUDED_PATH_PREFIXES = ['/api/hr/subadmin-activity', '/api/hr/manager-activity', '/api/auth/'];

interface PendingEntry {
  method: string;
  path: string;
  apiName?: string;
  actionLabel?: string;
  pageRoute?: string;
  statusCode?: number | null;
  actionEpochMs: number;
}

/** Kuyruk elemanı: kayıt + hedef endpoint (kuyruğa alındığı andaki role göre). */
interface QueuedEntry {
  endpoint: string;
  entry: PendingEntry;
}

// Türkçe işlem etiketi üretimi ve okuma-ucu dışlama activityLogDescriptions.ts
// modülünde (modal da eski kayıtlar için aynı çeviriciyi kullanır).

// ── Oturum rolü (token bazında cache'li decode) ──────────────────────────────
let cachedToken: string | null = null;
let cachedRole: string | null = null;

function getSessionRole(): string | null {
  const token = getStoredAccessToken();
  if (token !== cachedToken) {
    cachedToken = token;
    const claims = decodeJwt(token);
    cachedRole = typeof claims?.role === 'string' ? claims.role.toLowerCase() : null;
  }
  return cachedRole;
}

// ── Kuyruk + flush ───────────────────────────────────────────────────────────
let queue: QueuedEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;
let listenersInstalled = false;

function scheduleFlush(): void {
  if (queue.length >= FLUSH_BATCH_SIZE) {
    void flush();
    return;
  }
  if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, FLUSH_INTERVAL_MS);
  }
}

/** Kuyruktan alınan dilimi endpoint bazında gruplar (pratikte tek grup çıkar). */
function groupByEndpoint(batch: QueuedEntry[]): Map<string, PendingEntry[]> {
  const groups = new Map<string, PendingEntry[]>();
  for (const q of batch) {
    const list = groups.get(q.endpoint);
    if (list) list.push(q.entry);
    else groups.set(q.endpoint, [q.entry]);
  }
  return groups;
}

async function flush(): Promise<void> {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.splice(0, MAX_BATCH_SEND);
  try {
    const token = await getAccessToken();
    if (!token) {
      // Token şu an alınamadı (kısa süreli yenileme boşluğu / logout-login geçişi).
      // Kayıtları ÇÖPE ATMA — kuyruğa geri koy; oturum geri gelince gönderilir.
      queue = [...batch, ...queue].slice(0, MAX_QUEUE_SIZE);
      // eslint-disable-next-line no-console
      console.warn('[ActivityLogger] Oturum token\'ı yok — loglar kuyrukta bekletiliyor.');
      return;
    }
    for (const [endpoint, entries] of groupByEndpoint(batch)) {
      await axios.post(endpoint, entries, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 15000,
      });
    }
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 400 || status === 401 || status === 403) {
      // Kimlik/rol uyuşmazlığı (ör. kuyruktaki kayıtlar manager oturumundan ama artık
      // admin token'ı var) — tekrar denemek anlamsız, sonsuz döngüye girme.
      // eslint-disable-next-line no-console
      console.warn(`[ActivityLogger] Log ucu ${status} döndürdü — ${batch.length} kayıt atıldı.`, err?.response?.data);
    } else {
      // Ağ/sunucu hatası — kuyruğun başına geri koy, sonraki flush'ta tekrar dene.
      queue = [...batch, ...queue].slice(0, MAX_QUEUE_SIZE);
      // eslint-disable-next-line no-console
      console.warn('[ActivityLogger] Log gönderimi başarısız, tekrar denenecek:', err);
    }
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush();
  }
}

/**
 * Sekme kapanırken/gizlenirken bekleyen kayıtları keepalive fetch ile gönderir.
 * (axios keepalive desteklemez; fetch header'lı keepalive POST yapabilir.)
 */
function flushWithKeepalive(): void {
  if (queue.length === 0) return;
  const token = getStoredAccessToken();
  if (!token) return;
  const batch = queue.splice(0, MAX_BATCH_SEND);
  try {
    for (const [endpoint, entries] of groupByEndpoint(batch)) {
      void fetch(endpoint, {
        method: 'POST',
        keepalive: true,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(entries),
      });
    }
  } catch {
    // Kapanış anında hata — yapılabilecek bir şey yok.
  }
}

function installLifecycleListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('pagehide', flushWithKeepalive);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushWithKeepalive();
  });
  // SPA içi çıkış: ownAuthSession.clearSession token'ları silmeden ÖNCE bu event'i
  // yayınlar — bekleyen loglar mevcut token hâlâ localStorage'dayken gönderilir.
  // (Aksi halde logout→login geçişinde kuyruk kaybolur veya yanlış kimlikle akar.)
  window.addEventListener('gts:session-clearing', flushWithKeepalive);
}

// ── Genel API ────────────────────────────────────────────────────────────────

/**
 * apiClient response interceptor'ından her yanıt (başarı + hata) için çağrılır.
 * Yalnız izlenen rollerin (subadmin/manager — ROLE_LOG_ENDPOINTS) oturumundaki
 * yazma istekleri kuyruğa alınır; diğerleri no-op.
 */
export function recordApiWrite(params: {
  method?: string;
  baseURL?: string;
  url?: string;
  status?: number;
}): void {
  try {
    const method = (params.method || '').toUpperCase();
    if (!WRITE_METHODS.has(method)) return;
    const role = getSessionRole();
    const endpoint = role ? ROLE_LOG_ENDPOINTS[role] : undefined;
    if (!endpoint) return;

    // 401'ler loglanmaz: apiClient token yenileyip isteği tekrarlar — başarılı
    // tekrar zaten loglanır; yenileme başarısızsa oturum bitmiştir.
    if (params.status === 401) return;

    const rawUrl = params.url || '';
    const fullUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `${params.baseURL || ''}${rawUrl}`;

    let path = fullUrl;
    let apiName: string | undefined;
    try {
      const parsed = new URL(fullUrl);
      path = parsed.pathname;
      // Host'un ilk etiketi API adı olarak yeterli ayırt edicilikte (or. gtshrapi).
      apiName = parsed.hostname.split('.')[0] || undefined;
    } catch {
      // Göreli/bozuk URL — path olarak ham değeri kullan.
    }

    const lowerPath = path.toLowerCase();
    if (EXCLUDED_PATH_PREFIXES.some((p) => lowerPath.startsWith(p))) return;

    // POST olduğu halde veri DEĞİŞTİRMEYEN uçlar (sayfalı liste, rapor
    // çalıştırma, sorgulama...) loglanmaz — yönetici logunda gürültü olmasın.
    if (isReadOnlyApiPath(path)) return;

    queue.push({
      endpoint,
      entry: {
        method,
        path: path.slice(0, 500),
        apiName: apiName?.slice(0, 50),
        actionLabel: describeActivity(path, method).slice(0, 200),
        pageRoute:
          typeof window !== 'undefined' ? window.location.pathname.slice(0, 300) : undefined,
        statusCode: typeof params.status === 'number' ? params.status : null,
        actionEpochMs: Date.now(),
      },
    });
    if (queue.length > MAX_QUEUE_SIZE) {
      queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
    }

    installLifecycleListeners();
    scheduleFlush();
  } catch {
    // Loglama uygulama akışını ASLA bozmamalı — tüm hatalar yutulur.
  }
}
